import React, { useCallback, useEffect, useMemo, useState } from "react";
import HeaderBar from "components/ui/HeaderBar";
import RoleBasedSidebar from "components/ui/RoleBasedSidebar";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import Table from "components/ui/Table";
import BiodataDetailsView from "components/BiodataDetailsView";
import { useAuth } from "context/AuthContext";
import { employerAPI, biodataAPI } from "utils/api";
import { useBiodataCache } from "context/BiodataCacheContext";

const STATUS_BADGES = {
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  pending: "bg-amber-100 text-amber-700",
};

const STATUS_LABELS = {
  approved: "Approved",
  rejected: "Rejected",
  pending: "Pending Review",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const formatStatus = (status) => STATUS_LABELS[status] || status;

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "pending", label: "Pending" },
];

const EmployerApprovedWorkers = () => {
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
  });
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [vdrFilter, setVdrFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [activeBiodata, setActiveBiodata] = useState(null);
  const { getAllBiodatas, invalidateOnUpdate, getPhotoUrl } = useBiodataCache();

  const handleSidebarToggle = () => setSidebarCollapsed((prev) => !prev);

  const loadWorkers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        status: "selected",
        vdr_id: vdrFilter !== "all" ? vdrFilter : undefined,
        search: searchTerm.trim() || undefined,
      };
      const response = await getAllBiodatas(params);

      if (response.fromCache) {
      }

      setWorkers(response?.records || []);
      // The summary and groups are not available in the new API, so I will comment them out for now.
      // setSummary(response?.summary || { total: 0, approved: 0, pending: 0, rejected: 0 });
      // setGroups(response?.groups || []);
    } catch (err) {
      console.error("Failed to load approved workers", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load approved workers"
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter, vdrFilter, searchTerm]);

  useEffect(() => {
    loadWorkers();
  }, [loadWorkers]);

  useEffect(() => {
    const poll = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "visible"
      ) {
        loadWorkers();
      }
    };

    const interval = setInterval(poll, 60000); // reduce cadence and skip when tab hidden
    document.addEventListener("visibilitychange", poll);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", poll);
    };
  }, [loadWorkers]);

  const handleViewDetails = useCallback(async (worker) => {
    setDetailModalOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setActiveBiodata(null);

    try {
      const result = await biodataAPI.getBiodataById(worker.biodata_id);
      if (result.status === "success" && result.data) {
        setActiveBiodata(result.data);
      } else {
        setDetailError(result.message || "Failed to load biodata details.");
      }
    } catch (err) {
      console.error("Failed to fetch biodata details", err);
      setDetailError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load biodata details."
      );
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setActiveBiodata(null);
    setDetailLoading(false);
    setDetailError(null);
  };

  const filteredWorkers = useMemo(() => workers, [workers]);

  const vdrOptions = useMemo(() => {
    const options = groups
      .filter((group) => group?.vdr_id)
      .map((group) => ({
        value: group.vdr_id,
        label: `${group.vdr_id}${group.vdr_type ? ` • ${group.vdr_type}` : ""}`,
      }));
    return [{ value: "all", label: "All VDRs" }, ...options];
  }, [groups]);

  const tableColumns = useMemo(
    () => [
      {
        label: "Worker",
        render: (worker) => (
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
              <Image
                src={getPhotoUrl(worker.id, worker)}
                alt={worker.full_name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">
                {worker.full_name}
              </div>
              <div className="text-xs text-gray-500">
                Passport: {worker.passport_no || "—"}
              </div>
              {worker.worker_id && (
                <div className="text-xs text-gray-500">
                  Worker ID: {worker.worker_id}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-1">
                {worker.nationality || "—"}
                {worker.gender ? ` • ${worker.gender}` : ""}
              </div>
            </div>
          </div>
        ),
      },
      {
        label: "VDR Details",
        render: (worker) => (
          <div className="text-sm text-gray-600 space-y-1">
            <div className="font-medium text-gray-900">
              {worker.vdr.id || "—"}
            </div>
            <div className="text-xs text-gray-500">
              {worker.vdr.type || "No VDR Type"}
            </div>
            <div className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700">
              {worker.vdr.category?.label || "General"}
            </div>
          </div>
        ),
      },
      {
        label: "Status",
        render: (worker) => (
          <div className="space-y-2 text-sm">
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                STATUS_BADGES[worker.status] || "bg-gray-100 text-gray-600"
              }`}
            >
              {formatStatus(worker.status)}
            </span>
            {worker.rejection_reason && (
              <div className="text-xs text-red-600">
                Reason: {worker.rejection_reason}
              </div>
            )}
          </div>
        ),
      },
      {
        label: "Timeline",
        render: (worker) => (
          <div className="text-xs text-gray-500 space-y-1">
            <div>Submitted: {formatDateTime(worker.timeline.submitted_at)}</div>
            {worker.timeline.approved_at && (
              <div>Approved: {formatDateTime(worker.timeline.approved_at)}</div>
            )}
            {worker.timeline.rejected_at && (
              <div className="text-red-600">
                Rejected: {formatDateTime(worker.timeline.rejected_at)}
              </div>
            )}
            <div>Updated: {formatDateTime(worker.timeline.updated_at)}</div>
          </div>
        ),
      },
    ],
    []
  );

  const renderActions = useCallback(
    (worker) => (
      <div className="flex flex-col gap-2 min-w-[140px]">
        <button
          onClick={() => handleViewDetails(worker)}
          className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm font-medium flex items-center justify-center"
        >
          <Icon name="Eye" size={14} className="mr-1" />
          View Details
        </button>
      </div>
    ),
    [handleViewDetails]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderBar
        userRole="employer"
        onSidebarToggle={handleSidebarToggle}
        userName={user?.fullName || "Employer"}
      />

      <RoleBasedSidebar
        userRole="employer"
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <main
        className={`transition-all duration-300 ease-in-out pt-16 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-8 space-y-6">
          <BreadcrumbNavigation
            items={[
              { label: "Dashboard", path: "/employer-dashboard" },
              { label: "Selected Workers", path: "/employer-approved-workers" },
            ]}
          />

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Selected Workers
                </h1>
                <p className="text-gray-600">
                  Review the workers you have submitted to CLAB and track their
                  status.
                </p>
              </div>
              <button
                onClick={loadWorkers}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Icon name="RefreshCw" size={18} />
                <span>Refresh</span>
              </button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="text-xs text-blue-600 uppercase">Total</div>
                <div className="text-2xl font-semibold text-blue-800">
                  {summary.total}
                </div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <div className="text-xs text-green-600 uppercase">Approved</div>
                <div className="text-xl font-semibold text-green-800">
                  {summary.approved}
                </div>
              </div>
              <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                <div className="text-xs text-red-600 uppercase">Rejected</div>
                <div className="text-xl font-semibold text-red-800">
                  {summary.rejected}
                </div>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                <div className="text-xs text-amber-600 uppercase">Pending</div>
                <div className="text-xl font-semibold text-amber-800">
                  {summary.pending}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Status
                </label>
                <div className="flex flex-wrap gap-2">
                  {STATUS_FILTERS.map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => setStatusFilter(filter.value)}
                      className={`px-3 py-2 rounded-full text-xs font-semibold border ${
                        statusFilter === filter.value
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-gray-300 text-gray-600 hover:border-blue-400"
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  VDR
                </label>
                <select
                  value={vdrFilter}
                  onChange={(e) => setVdrFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {vdrOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="lg:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Search
                </label>
                <div className="relative">
                  <Icon
                    name="Search"
                    size={16}
                    className="absolute left-3 top-3 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search by name, passport, or ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") loadWorkers();
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <Icon
                name="Loader"
                size={48}
                className="animate-spin text-blue-600 mx-auto mb-4"
              />
              <p className="text-gray-600 text-lg">
                Loading approved workers...
              </p>
            </div>
          ) : filteredWorkers.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
              <Icon
                name="Users"
                size={48}
                className="text-gray-300 mx-auto mb-4"
              />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Workers Found
              </h3>
              <p className="text-gray-500">
                Try adjusting your search or filters.
              </p>
            </div>
          ) : (
            <Table
              data={filteredWorkers}
              columns={tableColumns}
              actions={renderActions}
              className="border border-gray-200 shadow-sm"
            />
          )}
        </div>
      </main>

      {detailModalOpen && (
        <>
          <style>{`
            @media print {
              .biodata-modal-overlay {
                display: none !important;
              }
              .biodata-modal-content {
                position: static !important;
                max-width: 100% !important;
                max-height: none !important;
                box-shadow: none !important;
              }
            }
          `}</style>
          <div className="fixed inset-0 bg-secondary-900 bg-opacity-50 flex items-center justify-center z-50 p-4 biodata-modal-overlay">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden biodata-modal-content">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Biodata Details
                </h2>
                <button
                  onClick={closeDetailModal}
                  className="p-2 text-gray-500 hover:text-gray-700 rounded"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[78vh]">
                {detailLoading ? (
                  <div className="flex items-center justify-center py-12 text-gray-600">
                    <Icon
                      name="Loader"
                      size={32}
                      className="animate-spin mr-3"
                    />
                    Loading biodata...
                  </div>
                ) : detailError ? (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
                    {detailError}
                  </div>
                ) : activeBiodata ? (
                  <BiodataDetailsView
                    biodata={activeBiodata}
                    title="Biodata Overview"
                  />
                ) : (
                  <div className="text-sm text-gray-500">
                    No biodata details available.
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmployerApprovedWorkers;
